import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="parallax"
export default class extends Controller {
  static targets = ["layer"]
  static values = {
    desktopBreakpoint: { type: Number, default: 1024 }
  }

  connect() {
    this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.handleScroll = this.scheduleUpdate.bind(this)
    this.handleResize = this.scheduleUpdate.bind(this)
    this.handleMotionPreferenceChange = this.update.bind(this)

    window.addEventListener("scroll", this.handleScroll, { passive: true })
    window.addEventListener("resize", this.handleResize)
    this.prefersReducedMotion.addEventListener("change", this.handleMotionPreferenceChange)

    this.scheduleUpdate()
  }

  disconnect() {
    window.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("resize", this.handleResize)
    this.prefersReducedMotion.removeEventListener("change", this.handleMotionPreferenceChange)

    if (this.frame) cancelAnimationFrame(this.frame)
  }

  scheduleUpdate() {
    if (this.frame) return

    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.update()
    })
  }

  update() {
    if (this.prefersReducedMotion.matches) {
      this.resetLayers()
      return
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight

    this.layerTargets.forEach((layer, index) => {
      const rect = layer.getBoundingClientRect()

      if (rect.bottom <= 0 || rect.top >= viewportHeight) {
        layer.style.transform = ""
        layer.style.willChange = ""
        return
      }

      const layerCenter = rect.top + (rect.height / 2)
      const viewportCenter = viewportHeight / 2
      const progress = (layerCenter - viewportCenter) / viewportHeight
      const strength = this.strengthFor(layer)
      const defaultDirection = index % 2 === 0 ? "1" : "-1"
      const direction = Number.parseFloat(layer.dataset.parallaxDirection || defaultDirection)
      const shift = Math.max(Math.min(progress * strength * direction, strength), -strength)

      layer.style.transform = `translate3d(0, ${shift}px, 0) scale(1.08)`
      layer.style.willChange = "transform"
    })
  }

  resetLayers() {
    this.layerTargets.forEach((layer) => {
      layer.style.transform = ""
      layer.style.willChange = ""
    })
  }

  strengthFor(layer) {
    const desktopStrength = Number.parseFloat(layer.dataset.parallaxDesktopStrength || "")
    const mobileStrength = Number.parseFloat(layer.dataset.parallaxMobileStrength || "")
    const fallbackStrength = Number.parseFloat(layer.dataset.parallaxStrength || "48")
    const isDesktop = window.innerWidth >= this.desktopBreakpointValue

    if (isDesktop && !Number.isNaN(desktopStrength)) return desktopStrength
    if (!isDesktop && !Number.isNaN(mobileStrength)) return mobileStrength
    if (!Number.isNaN(desktopStrength) && Number.isNaN(mobileStrength)) return desktopStrength
    if (!Number.isNaN(mobileStrength) && Number.isNaN(desktopStrength)) return mobileStrength

    return fallbackStrength
  }
}
